import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from './Toast';
import { timeAgo } from '../lib/constants';

const IS_DESKTOP = () => window.innerWidth >= 768;

const DEV_ROOM_ID = '00000000-0000-0000-0000-000000000002';
const LS_KEY = 'shoptrack_dev_todos';

const DEFAULT_DEV_TODOS = [
  { id: 'td-1', room_id: DEV_ROOM_ID, claim_id: 'dev-1', claim_label: '2021 Toyota Camry · ABC1234', text: 'Call State Farm re: hood shipment ETA', done: false, done_at: null, created_at: '2026-06-21T07:00:00Z' },
  { id: 'td-2', room_id: DEV_ROOM_ID, claim_id: 'dev-7', claim_label: '2017 Nissan Altima · NIS7723', text: 'Follow up with Farmers on supplement approval', done: false, done_at: null, created_at: '2026-06-21T08:00:00Z' },
  { id: 'td-3', room_id: DEV_ROOM_ID, claim_id: null, claim_label: null, text: 'Order more masking tape and sandpaper', done: false, done_at: null, created_at: '2026-06-20T09:00:00Z' },
  { id: 'td-4', room_id: DEV_ROOM_ID, claim_id: 'dev-13', claim_label: '2023 Ram 1500 · RAM2301', text: 'Confirm rental extension for client', done: false, done_at: null, created_at: '2026-06-20T10:00:00Z' },
  { id: 'td-5', room_id: DEV_ROOM_ID, claim_id: null, claim_label: null, text: 'Schedule alignment rack for next week', done: true, done_at: '2026-06-20T15:00:00Z', created_at: '2026-06-19T09:00:00Z' },
];

function devLoad() {
  try {
    const saved = localStorage.getItem(LS_KEY);
    return saved ? JSON.parse(saved) : DEFAULT_DEV_TODOS;
  } catch { return DEFAULT_DEV_TODOS; }
}

function devSave(todos) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(todos)); } catch {}
}

export default function TodoPanel({ room, session, claims, onClose, onOpenClaim, desktop: desktopProp }) {
  const desktop = desktopProp ?? IS_DESKTOP();
  const isDev = room.id === DEV_ROOM_ID;

  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [selectedClaim, setSelectedClaim] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null); // shrink-out animation before delete

  const loadTodos = useCallback(async () => {
    if (isDev) { setTodos(devLoad()); setLoading(false); return; }
    const { data } = await supabase
      .from('todos')
      .select('*')
      .eq('room_id', room.id)
      .eq('user_id', session.user.id)
      .order('done', { ascending: true })
      .order('created_at', { ascending: false });
    if (data) setTodos(data);
    setLoading(false);
  }, [room.id, session.user.id, isDev]);

  useEffect(() => {
    loadTodos();
    if (isDev) return;
    const ch = supabase.channel(`todos-${room.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'todos', filter: `room_id=eq.${room.id}` }, loadTodos)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [room.id, loadTodos, isDev]);

  function devUpdate(updater) {
    setTodos(prev => {
      const next = updater(prev);
      devSave(next);
      return next;
    });
  }

  async function addTodo(e) {
    e.preventDefault();
    if (!text.trim()) return;
    setAdding(true);
    const claimObj = claims.find(c => c.id === selectedClaim);
    const claimLabel = claimObj ? `${claimObj.car} · ${claimObj.plate}` : null;
    if (isDev) {
      const newTodo = {
        id: `td-${Date.now()}`, room_id: room.id,
        claim_id: selectedClaim || null, claim_label: claimLabel,
        text: text.trim(), done: false, done_at: null,
        created_at: new Date().toISOString(),
      };
      devUpdate(prev => [newTodo, ...prev]);
    } else {
      // Optimistic: show the task and clear the form immediately
      const row = {
        room_id: room.id,
        user_id: session.user.id,
        claim_id: selectedClaim || null,
        claim_label: claimLabel,
        text: text.trim(),
        done: false,
      };
      const temp = { ...row, id: `temp-${Date.now()}`, done_at: null, created_at: new Date().toISOString() };
      const prevText = text, prevClaim = selectedClaim;
      setTodos(prev => [temp, ...prev]);
      setText('');
      setSelectedClaim('');
      setAdding(false);
      const { data, error } = await supabase.from('todos').insert(row).select().single();
      if (error) {
        setTodos(prev => prev.filter(t => t.id !== temp.id));
        setText(prevText);
        setSelectedClaim(prevClaim);
        toast('Could not add the task — check your connection and try again.', 'error');
        return;
      }
      setTodos(prev => prev.map(t => (t.id === temp.id ? data : t)));
      return;
    }
    setText('');
    setSelectedClaim('');
    setAdding(false);
  }

  async function toggleDone(todo) {
    const nowDone = !todo.done;
    const patch = { done: nowDone, done_at: nowDone ? new Date().toISOString() : null };
    if (isDev) {
      devUpdate(prev => prev.map(t => t.id === todo.id ? { ...t, ...patch } : t));
      return;
    }
    // Optimistic: flip immediately, revert if the write fails
    setTodos(prev => prev.map(t => (t.id === todo.id ? { ...t, ...patch } : t)));
    const { error } = await supabase.from('todos').update(patch).eq('id', todo.id);
    if (error) {
      setTodos(prev => prev.map(t => (t.id === todo.id ? { ...t, done: todo.done, done_at: todo.done_at } : t)));
      toast('Could not update the task — check your connection and try again.', 'error');
    }
  }

  async function deleteTodo(id) {
    if (!window.confirm('Delete this task?')) return;
    // Shrink the row away, then remove it
    setRemovingId(id);
    await new Promise(r => setTimeout(r, 180));
    setRemovingId(null);
    if (isDev) { devUpdate(prev => prev.filter(t => t.id !== id)); return; }
    const removed = todos.find(t => t.id === id);
    // Optimistic: remove immediately, restore if the delete fails
    setTodos(prev => prev.filter(t => t.id !== id));
    const { error } = await supabase.from('todos').delete().eq('id', id);
    if (error && removed) {
      setTodos(prev => [removed, ...prev]);
      toast('Could not delete the task — check your connection and try again.', 'error');
    }
  }

  const open = todos.filter(t => !t.done);
  const done = todos.filter(t => t.done);

  return (
    <div className="modal-overlay" style={{ zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: desktop ? 32 : 0, background: 'rgba(5,5,6,0.85)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-panel" style={{ background: 'var(--bg2)', border: '1px solid var(--border)', maxWidth: 860, width: '100%', maxHeight: desktop ? '90vh' : undefined, display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', height: 72, padding: '0 24px', boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)', flexShrink: 0 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: desktop ? 26 : 19, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
            Shop Tasks
          </div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} className="raise-3d" style={{ background: 'var(--bg3)', border: 'none', color: 'var(--text2)', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
</button>
        </div>

        {/* Add form */}
        <div style={{ padding: '16px 20px', boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <form onSubmit={addTodo} style={{ position: 'relative' }}>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addTodo(e); } }}
              placeholder="Add a task... (Enter to save, Shift+Enter for new line)"
              rows={4}
              style={{ width: '100%', resize: 'none', fontSize: 15, lineHeight: 1.6, padding: '12px 52px 12px 14px', boxSizing: 'border-box' }}
            />
            <button type="submit" disabled={adding || !text.trim()} className="raise-3d-orange" style={{ position: 'absolute', bottom: 20, right: 10, background: 'var(--orange)', color: '#16100b', border: 'none', borderRadius: 8, width: 36, height: 36, fontSize: 24, fontWeight: 700, cursor: text.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, opacity: text.trim() ? 1 : 0.35, transition: 'opacity 0.15s' }}>
              {adding ? <div className="spinner" style={{ width: 14, height: 14 }} /> : <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>}
            </button>
          </form>
          <select value={selectedClaim} onChange={e => setSelectedClaim(e.target.value)} style={{ fontSize: 13, padding: '7px 10px', fontFamily: 'var(--font-body)' }}>
            <option value="">No claim linked</option>
            {claims.filter(c => c.status !== 'Closed').map(c => (
              <option key={c.id} value={c.id}>{c.car} · {c.plate}</option>
            ))}
          </select>
        </div>

        {/* List */}
        <div className="modal-scroll" style={{ flex: 1, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {loading && <div className="empty-state"><div className="spinner" /></div>}
          {!loading && open.length === 0 && done.length === 0 && (
            <div className="empty-state">
              <div className="icon">✅</div>
              <div className="title">No tasks yet</div>
              <div className="sub">Add a task above to get started</div>
            </div>
          )}

          {open.map(todo => <TodoItem key={todo.id} todo={todo} removing={removingId === todo.id} onToggle={toggleDone} onDelete={deleteTodo} onOpenClaim={onOpenClaim} />)}

          {done.length > 0 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0 4px' }}>
                <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1px', color: 'var(--text3)', textTransform: 'uppercase' }}>Done ({done.length})</span>
                <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              </div>
              {done.map(todo => <TodoItem key={todo.id} todo={todo} removing={removingId === todo.id} onToggle={toggleDone} onDelete={deleteTodo} onOpenClaim={onOpenClaim} />)}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TodoItem({ todo, removing, onToggle, onDelete, onOpenClaim }) {
  return (
    <div className={removing ? 'removing' : undefined} style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      background: todo.done ? 'transparent' : 'var(--bg3)',
      border: `1px solid ${todo.done ? 'transparent' : 'var(--border)'}`,
      borderRadius: 10, padding: todo.done ? '6px 4px' : '10px 12px',
      opacity: todo.done ? 0.5 : 1, transition: 'all 0.15s',
    }}>
      <button onClick={() => onToggle(todo)} style={{
        width: 20, height: 20, borderRadius: 5, flexShrink: 0, marginTop: 1,
        border: `2px solid ${todo.done ? 'var(--green)' : 'var(--border)'}`,
        background: todo.done ? 'var(--green)' : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', color: '#fff', fontSize: 12, fontWeight: 800,
      }}>{todo.done ? '✓' : ''}</button>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.4, textDecoration: todo.done ? 'line-through' : 'none', color: todo.done ? 'var(--text3)' : 'var(--text)' }}>
          {todo.text}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 5, alignItems: 'center' }}>
          {todo.claim_id && todo.claim_label && (
            <button onClick={() => onOpenClaim && onOpenClaim(todo.claim_id)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.3)',
              borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 600,
              color: 'var(--orange)', cursor: 'pointer',
            }}>
              🔗 {todo.claim_label}
            </button>
          )}
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>{timeAgo(todo.created_at)}</span>
        </div>
      </div>

      <button onClick={() => onDelete(todo.id)} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 16, padding: '0 2px', flexShrink: 0, opacity: 0.6 }}>×</button>
    </div>
  );
}
